import { AsyncSelect } from "chakra-react-select";
import { FormControl, Text } from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { useQuizConfig } from "../public/quizProvider";
import { useSlide } from "../public/slide";
import { SlidePropsLocation, LocationValue } from "../public/types";
import { LocationState, useQuizActions, useQuizSnapshot } from "./state";
import { debounce } from "./utils";
import { commonInputStyles } from "./commonInput";
import { MyFormLabel } from "./ui";
import React from "react";
import { useRootStore } from "@base/RootStore";
import { useTranslation } from "gatsby-plugin-react-i18next";

type SelectOptionType = LocationValue;
type LocationApiResult = Array<LocationApiOption> | { error: string };
type LocationApiOption = {
  address: { country: string; name: string; state: string };
  lat: string;
  lon: string;
  place_id: string;
  boundingbox: [string, string, string, string];
};

const getApiURL = (apiKey: string, query: string) => {
  const places = ["city", "town"];
  const placeTags = places.map((p) => `place:${p}`).join(",");

  const p = new URLSearchParams();
  p.set("q", query);
  p.set("key", apiKey);
  p.set("tag", placeTags);

  return `https://api.locationiq.com/v1/autocomplete?${p.toString()}`;
};

async function fetchOptions({
  locationApiKey,
  inputValue,
  coordinates,
}: {
  locationApiKey: string;
  inputValue: string;
  coordinates?: { lat: number; lon: number };
}): Promise<SelectOptionType[] | { error: { message: string; [prop: string]: any } }> {
  try {
    const res = await fetch(getApiURL(locationApiKey, inputValue));
    const json = (await res.json()) as LocationApiResult;

    // this is ok for us, not an error
    if (res.status === 404) {
      return [];
    }

    if ("error" in json) {
      return {
        error: {
          message: json.error,
          status: res.status,
        },
      };
    }

    const filtered = !coordinates
      ? json
      : json.filter((it) => {
          const [lat_1, lat_2, lon_1, lon_2] = it.boundingbox;
          const { lat, lon } = coordinates;

          return (
            lat > Number(lat_1) && lat < Number(lat_2) && lon > Number(lon_1) && lon < Number(lon_2)
          );
        });

    return filtered.map((it) => {
      const formattedLabel = [it.address.name, it.address.state, it.address.country].join(", ");

      return {
        formattedText: formattedLabel,
        placeID: it.place_id,
        lat: Number(it.lat),
        long: Number(it.lon),
      };
    });
  } catch (err) {
    return {
      error: {
        message: "fetch location options unknown error",
        originalErr: err,
      },
    };
  }
}

export function Location({
  placeholder,
  locationApiKey,
}: {
  locationApiKey: string;
} & SlidePropsLocation) {
  const quizConfig = useQuizConfig();
  const actions = useQuizActions();
  const slide = useSlide();
  const snap = useQuizSnapshot();
  const { geolocation } = useRootStore();
  const [inputValue, setInputValue] = useState("");
  const { t } = useTranslation();

  const state = snap.currentSlideState as LocationState;
  const inputStyles = commonInputStyles();

  React.useEffect(() => {
    if (!(snap.currentSlideState as LocationState).value) {
      const { city, lat, lon } = geolocation;

      if (city) {
        loadOptionsDebounced({
          inputValue: city,
          coordinates: lat && lon ? { lat, lon } : undefined,
          callback: (options) => {
            const option = options[0];
            if (option) {
              actions.setLocationValue(slide.id, option ?? undefined);
            }
          },
        });
      }
    }
  }, []);

  const loadOptionsDebounced = useCallback(
    debounce(
      ({
        inputValue,
        callback,
        coordinates,
      }: {
        inputValue: string;
        callback: (options: SelectOptionType[]) => void;
        coordinates?: { lat: number; lon: number };
      }) => {
        fetchOptions({
          locationApiKey,
          inputValue,
          coordinates,
        }).then((result) => {
          if ("error" in result) {
            quizConfig.onErrorEvent?.({ name: "error", data: result.error });
            callback([]);
            return;
          }

          callback(result);
        });
      },
      350
    ),
    []
  );

  return (
    <FormControl>
      <MyFormLabel>{t("quizLib.location.city", { defaultValue: "City" })}</MyFormLabel>
      <AsyncSelect
        size={"lg"}
        chakraStyles={{
          container: (provided) => ({
            ...provided,
            width: "full",
          }),
          control: (provided) => ({
            ...provided,
            ...inputStyles,
          }),
          placeholder: (provided) => ({
            ...provided,
            ...inputStyles._placeholder,
          }),
          menuList: (provided) => ({
            ...provided,
            backgroundColor: "bg.100",
          }),
          option: (provided) => ({
            ...provided,
            backgroundColor: "bg.100",
            fontSize: "sm",
            _hover: { backgroundColor: "bg.50" },
          }),
          loadingMessage: (provided) => ({
            ...provided,
            color: "text.500",
            fontSize: "sm",
          }),
        }}
        cacheOptions
        noOptionsMessage={() => {
          return (
            <Text fontSize={"sm"} color="text.500">
              {t("quizLib.location.noLocations", { defaultValue: "No locations found" })}
            </Text>
          );
        }}
        onChange={(e) => {
          actions.setLocationValue(slide.id, e ?? undefined);
        }}
        onInputChange={(val) => {
          setInputValue(val);
        }}
        onFocus={() => {
          setInputValue(" "); // empty string doesn't work, so using a single space here
        }}
        inputValue={inputValue}
        isClearable
        isSearchable
        blurInputOnSelect
        getOptionLabel={(o) => o.formattedText}
        getOptionValue={(o) => o.placeID}
        value={state.value}
        openMenuOnClick={false}
        loadOptions={(inputValue, callback) => {
          loadOptionsDebounced({ inputValue, callback });
        }}
        placeholder={placeholder}
        components={{
          DropdownIndicator: null,
        }}
      />
    </FormControl>
  );
}
